import { QueryFunctionContext } from '@tanstack/react-query';
import { getArtifactsResults } from 'api/platform';
import {
  isWaitingForArtifactResults,
  useArtifactResultsQuery,
} from 'components/StudyDashboard/ProceduresPage/Actions/PostProcessingAndCalculateFeatures/useArtifactsResultsQuery';
import { concat, every, flatMap, isEmpty, size, some, times } from 'es-toolkit/compat';
import { ModelInference, ResultNameOrchestrations } from 'interfaces/calculateFeatures';
import {
  AreaConfig,
  AreaQuantificationConfig,
  BinaryClassifierConfig,
  CellAttributesTableConfig,
  CellConfig,
  CellQuantificationConfig,
  CellSegmentationConfig,
  ClassificationConfig,
  ClassificationIntensityConfig,
  DefectConfig,
  PipsAreaInstancesConfig,
  PipsResultsConfig,
  SlideDetectionsTableConfig,
  SlideSegmentationsTableConfig,
  TlsConfig,
} from 'interfaces/inferenceArtifacts';
import { useMemo } from 'react';
import { getArtifactResultsErrorMessage } from 'utils/artifactValidationError';
import { CasesParams } from 'utils/useCasesParams/types';
import { useQueriesWithErrorAndRetrySnackbar } from 'utils/useQueryWithErrorAndRetrySnackbar';

const basicInferenceArtifactConfigs = [
  AreaConfig,
  CellConfig,
  DefectConfig,
  TlsConfig,
  ClassificationIntensityConfig,
  ClassificationConfig,
  CellSegmentationConfig,
];

export const useArtifactQueries = ({
  casesParams,
  isMultiplex,
}: {
  casesParams: CasesParams;
  isMultiplex: boolean;
}) => {
  const areArtifactQueriesEnabled = Boolean(casesParams?.studyId);
  const inferenceArtifactConfigs = isMultiplex
    ? [...basicInferenceArtifactConfigs, BinaryClassifierConfig]
    : basicInferenceArtifactConfigs;

  const inferenceArtifactsQueries = useQueriesWithErrorAndRetrySnackbar({
    queries: times(size(inferenceArtifactConfigs), (index) => {
      const inferenceArtifactsParams = {
        ...casesParams,
        configParams: inferenceArtifactConfigs[index],
      };
      return {
        errorMessageGetter: getArtifactResultsErrorMessage,
        queriedEntityName: 'artifact inputs',
        queryKey: ['artifactsResults', inferenceArtifactsParams],
        queryFn: ({ signal }: QueryFunctionContext) => getArtifactsResults(inferenceArtifactsParams, signal),
        enabled: areArtifactQueriesEnabled,
        placeholderData: [] as ModelInference[],
        retry: false,
      };
    }),
  });
  const inferenceArtifacts = flatMap(inferenceArtifactsQueries, (query) => query.data);
  const isInferenceArtifactsLoading =
    !isEmpty(inferenceArtifactsQueries) &&
    some(inferenceArtifactsQueries, (query) =>
      isWaitingForArtifactResults({
        enabled: areArtifactQueriesEnabled,
        isFetched: query.isFetched,
        isFetching: query.isFetching,
        isLoading: query.isLoading,
        isPlaceholderData: query.isPlaceholderData,
      })
    );
  const isInferenceArtifactsFetched =
    !isEmpty(inferenceArtifactsQueries) && every(inferenceArtifactsQueries, 'isFetched');
  const isInferenceArtifactsFetching =
    !isEmpty(inferenceArtifactsQueries) && some(inferenceArtifactsQueries, 'isFetching');
  const isInferenceArtifactsError = !isEmpty(inferenceArtifactsQueries) && some(inferenceArtifactsQueries, 'isError');

  const {
    data: postprocessedArtifacts,
    isWaitingForResults: isPostprocessedArtifactsLoading,
    isFetched: isPostprocessedArtifactsFetched,
    isFetching: isPostprocessedArtifactsFetching,
    isError: isPostprocessedArtifactsError,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: casesParams,
    config: CellAttributesTableConfig,
    enabled: areArtifactQueriesEnabled,
  });

  const {
    data: pipsResultsArtifacts,
    isWaitingForResults: isPipsResultsArtifactsLoading,
    isFetched: isPipsResultsArtifactsFetched,
    isFetching: isPipsResultsArtifactsFetching,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: casesParams,
    config: PipsResultsConfig,
    enabled: areArtifactQueriesEnabled,
  });

  const {
    data: pipsAreaInstancesArtifacts,
    isWaitingForResults: isPipsAreaInstancesArtifactsLoading,
    isFetched: isPipsAreaInstancesArtifactsFetched,
    isFetching: isPipsAreaInstancesArtifactsFetching,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: casesParams,
    config: PipsAreaInstancesConfig,
    enabled: areArtifactQueriesEnabled,
  });

  const {
    data: cellQuantificationArtifacts,
    isWaitingForResults: isCellQuantificationArtifactsLoading,
    isFetched: isCellQuantificationArtifactsFetched,
    isFetching: isCellQuantificationArtifactsFetching,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: casesParams,
    config: CellQuantificationConfig,
    enabled: areArtifactQueriesEnabled,
  });

  const {
    data: areaQuantificationArtifacts,
    isWaitingForResults: isAreaQuantificationArtifactsLoading,
    isFetched: isAreaQuantificationArtifactsFetched,
    isFetching: isAreaQuantificationArtifactsFetching,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: casesParams,
    config: AreaQuantificationConfig,
    enabled: areArtifactQueriesEnabled,
  });

  const {
    data: slideSegmentationsTableArtifacts,
    isWaitingForResults: isSlideSegmentationsTableArtifactsLoading,
    isFetched: isSlideSegmentationsTableArtifactsFetched,
    isFetching: isSlideSegmentationsTableArtifactsFetching,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: casesParams,
    config: SlideSegmentationsTableConfig,
    enabled: areArtifactQueriesEnabled,
  });

  const {
    data: slideDetectionsTableArtifacts,
    isWaitingForResults: isSlideDetectionsTableArtifactsLoading,
    isFetched: isSlideDetectionsTableArtifactsFetched,
    isFetching: isSlideDetectionsTableArtifactsFetching,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: casesParams,
    config: SlideDetectionsTableConfig,
    enabled: areArtifactQueriesEnabled,
  });

  const allNonModelArtifacts = useMemo(
    () =>
      concat(
        postprocessedArtifacts,
        pipsResultsArtifacts,
        pipsAreaInstancesArtifacts,
        cellQuantificationArtifacts,
        areaQuantificationArtifacts,
        slideSegmentationsTableArtifacts,
        slideDetectionsTableArtifacts
      ),
    [
      postprocessedArtifacts,
      pipsResultsArtifacts,
      pipsAreaInstancesArtifacts,
      cellQuantificationArtifacts,
      areaQuantificationArtifacts,
      slideSegmentationsTableArtifacts,
      slideDetectionsTableArtifacts,
    ]
  );

  return {
    inferenceArtifacts,
    allNonModelArtifacts,
    postprocessedArtifacts,
    pipsResultsArtifacts,
    pipsAreaInstancesArtifacts,
    cellQuantificationArtifacts,
    areaQuantificationArtifacts,
    slideSegmentationsTableArtifacts,
    slideDetectionsTableArtifacts,
    isInferenceArtifactsLoading,
    isInferenceArtifactsFetched,
    isInferenceArtifactsFetching,
    isInferenceArtifactsError,
    isPostprocessedArtifactsLoading,
    isPostprocessedArtifactsFetched,
    isPostprocessedArtifactsFetching,
    isPostprocessedArtifactsError,
    isPipsResultsArtifactsLoading,
    isPipsResultsArtifactsFetched,
    isPipsResultsArtifactsFetching,
    isPipsAreaInstancesArtifactsLoading,
    isPipsAreaInstancesArtifactsFetched,
    isPipsAreaInstancesArtifactsFetching,
    isCellQuantificationArtifactsLoading,
    isCellQuantificationArtifactsFetched,
    isCellQuantificationArtifactsFetching,
    isAreaQuantificationArtifactsLoading,
    isAreaQuantificationArtifactsFetched,
    isAreaQuantificationArtifactsFetching,
    isSlideSegmentationsTableArtifactsLoading,
    isSlideSegmentationsTableArtifactsFetched,
    isSlideSegmentationsTableArtifactsFetching,
    isSlideDetectionsTableArtifactsLoading,
    isSlideDetectionsTableArtifactsFetched,
    isSlideDetectionsTableArtifactsFetching,
  };
};
